import React, {useState, useEffect} from "react";
import {useTitleFromComponent, useDataWithRoute} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {VsiBubble, VsiH4,} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {Row, Col, Spinner, Card} from "reactstrap";
import Grid from "../../../components/Grid";
import ProgressBar from "../../../components/ProgressBar";
import {provisioningColorsNoSnap, formatBarData} from "../../../utils/chartUtils";
import StackedColumnChart from "../../../components/StackedColumnChart";
import {numberFormatter} from "../../../utils/gridFormatters";

const allUnits = [
    {value: 'device_type', label: 'Device Type'},
    {value: 'subscription', label: 'Subscription'},
    {value: 'resource_group', label: 'Resource Group'},
];

const progressDef = {
    type: "single",
    fields: ["used_pct"],
    thresholds: [
        [80, "#008080"],
        [100, "#d80000"],
    ],
    multiply: false,
    denominator: 100,
    title: "(% Used of Usable)",
};

const cloudStorageDefs = [
    {
        "field": "cloud_storage",
        "headerName": "Cloud Storage"
    },
    {
        "field": "total_capacity",
        "type": "numericColumn",
        "headerName": "Total Capacity",
        "aggFunc": "sum",
        "filter": "agNumberColumnFilter",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "total_alloc",
        "type": "numericColumn",
        "headerName": "Total Used",
        "aggFunc": "sum",
        "filter": "agNumberColumnFilter",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "total_free",
        "type": "numericColumn",
        "headerName": "Total Free",
        "aggFunc": "sum",
        "filter": "agNumberColumnFilter",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
];

const sortingArr = ['Total Free', 'Total Used'];

export default function CloudStorageByGroup({context, title}) {

    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, periodId} = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const searchUnit = searchParams.get('unit');
    let defaultUnit = searchUnit ? allUnits.find(({value}) => searchUnit === value) : allUnits[0];
    const [unit, setUnit] = useState(defaultUnit);
    const [barData, setBarData] = useState({
        categories: [],
        series: []
    });

    const [defs, setDefs] = useState(cloudStorageDefs);

    const [data, {isLoading}] = useDataWithRoute(`/api/v6/report/enterprise_cloud_storage_by_${unit.value}_proc?client_id=${client.clientId}&period_id=${periodId}`)

    useEffect(() => {
        setBarData(formatBarData(data?.recordsets?.[0] || [], {
            sortingArr,
            sortingField: unit.value,
            defs: cloudStorageDefs.filter(def => sortingArr.includes(def.headerName)),
        }));
    }, [data]);

    useEffect(() => {
        const uDefs = [...defs];
        uDefs.shift();
        uDefs.unshift({
            field: unit.value,
            headerName: unit.label
        });
        setDefs(uDefs);
    }, [unit])

    const onUnitChange = (selectedUnit) => {
        setUnit(selectedUnit);
        setSearchParams({
            unit: selectedUnit.value,
        }, {replace: true});
    };

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        setBarData(formatBarData(rowData, {
            sortingArr,
            sortingField: unit.value,
            defs: cloudStorageDefs.filter(def => sortingArr.includes(def.headerName)),
        }));
    }

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Storage by {unit.label}</VsiH4>
        <VsiBubble noAutobot>
            <VsiLabeledSelect options={allUnits} label={'Select Unit'} onChange={onUnitChange} defaultValue={unit}
                              style={{width: '120px'}}/>
        </VsiBubble>
        <Row>
            <Col className={`p-2 pb-0`} style={{maxHeight: 300, overflowY: 'scroll'}}>
                <div className={"d-flex flex-column justify-content-center"}>
                    {isLoading ? <Spinner/> : data?.[0]?.map?.((datum, index) => {
                        return (
                            <ProgressBar
                                className={"mb-1"}
                                data={[datum[progressDef.fields[0]]]}
                                denominator={progressDef.denominator}
                                title={datum[unit.value] ? datum[unit.value] : ''}
                                colors={progressDef.colors}
                                key={`progress-bar-${datum?.[unit.value]
                                    ?.toLowerCase?.()
                                    ?.split?.(" ")
                                    ?.join?.("-")}-${index}`}
                                isMulti={progressDef.type === "multi"}
                                multiply={progressDef.multiply}
                                size={"lg"}/>
                        )
                    })}
                </div>
            </Col>
        </Row>
        <Row>
            <Col xs={12} lg={6}>
                <VsiBubble unit={unit.value} autobotInfo={{yAxisTitle: 'Size'}}>
                    <StackedColumnChart categories={barData.categories}
                                        colors={provisioningColorsNoSnap}
                                        height={805}
                                        series={barData.series}/>
                </VsiBubble>
            </Col>
            <Col xs={12} lg={6}>
                <VsiBubble unit={unit.value}>
                    <Grid data={data?.recordsets?.[0] || []}
                          saveParams={{unit: unit.value}}
                          columnDefs={defs}
                          filterChanged={filterChanged}
                    />
                </VsiBubble>
            </Col>
        </Row>
    </Card>
}
